import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { UserPlus, Users, Loader2, GraduationCap } from 'lucide-react';

export const AddPlayersDialog = ({
  open, onOpenChange, tournament, enrolledPlayers, selectedPlayerIds, loadingPlayers,
  addingPlayers, categories, setCategories, activeCategory, setActiveCategory,
  searchTerm, setSearchTerm, notifyParents, setNotifyParents,
  sortField, setSortField, sortOrder, setSortOrder,
  // When `includeUnenrolled` is true, the available-players endpoint
  // returns graduates / seasonal players too. Each row is tagged with
  // `is_enrolled=false` so we can show a "Graduated" badge.
  includeUnenrolled = false, setIncludeUnenrolled,
  onTogglePlayer, onSelectAll, onFetchPlayers, onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-emerald-600" />Add Players to Tournament</DialogTitle>
        <DialogDescription>Select enrolled players to register for this tournament. Toggle "Include graduated / unenrolled" if you also need to roster academy alumni.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="bg-slate-50 p-4 rounded-lg">
          <h4 className="font-medium text-slate-900">{tournament?.name}</h4>
          <p className="text-sm text-slate-500">{tournament?.start_date} - {tournament?.location_type === 'local' ? 'Local' : 'Travel'}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Age Categories</Label>
            <div className="flex flex-wrap gap-2">
              {tournament?.age_categories?.map(cat => (
                <label key={cat} className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox checked={categories.includes(cat)} onCheckedChange={(checked) => {
                    const newCats = checked ? [...categories, cat] : categories.filter(c => c !== cat);
                    setCategories(newCats);
                    if (newCats.length > 0) setActiveCategory(newCats[0]);
                    onFetchPlayers(newCats);
                  }} />
                  <span className="text-sm font-medium">{cat.toUpperCase()}</span>
                </label>
              ))}
              <Button variant="outline" size="sm" onClick={() => {
                const allCats = tournament?.age_categories || [];
                setCategories(allCats);
                if (allCats.length > 0) setActiveCategory(allCats[0]);
                onFetchPlayers(allCats);
              }} className="text-xs h-7">Select All</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Search Player</Label>
            <Input placeholder="Type player name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="player-search-input" />
          </div>
          <div className="space-y-2">
            <Label>Notify Parents</Label>
            <div className="flex items-center gap-2 mt-2">
              <Switch checked={notifyParents} onCheckedChange={setNotifyParents} id="notify-parents" />
              <Label htmlFor="notify-parents" className="font-normal text-sm text-slate-600">Send email notification</Label>
            </div>
            {setIncludeUnenrolled && (
              <div className="flex items-center gap-2 mt-1">
                <Switch
                  checked={includeUnenrolled}
                  onCheckedChange={setIncludeUnenrolled}
                  id="include-unenrolled"
                  data-testid="include-unenrolled-toggle"
                />
                <Label htmlFor="include-unenrolled" className="font-normal text-sm text-slate-600 flex items-center gap-1">
                  <GraduationCap className="h-3.5 w-3.5 text-slate-500" />
                  Include graduated / unenrolled players
                </Label>
              </div>
            )}
          </div>
        </div>
        <div className="border rounded-lg">
          <div className="p-3 bg-slate-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={enrolledPlayers.length > 0 && selectedPlayerIds.length === enrolledPlayers.filter(p => p.full_name?.toLowerCase().includes(searchTerm.toLowerCase())).length}
                onCheckedChange={onSelectAll}
              />
              <span className="text-sm font-medium">Available Players ({enrolledPlayers.filter(p => p.full_name?.toLowerCase().includes(searchTerm.toLowerCase())).length})</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Sort by:</Label>
                <Select value={sortField} onValueChange={setSortField}>
                  <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_name">Name</SelectItem>
                    <SelectItem value="age">Age</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="h-8 px-2">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</Button>
              </div>
              {selectedPlayerIds.length > 0 && <Badge className="bg-emerald-100 text-emerald-700">{selectedPlayerIds.length} selected</Badge>}
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {loadingPlayers ? (
              <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /><p className="text-sm text-slate-500 mt-2">Loading players...</p></div>
            ) : enrolledPlayers.length === 0 ? (
              <div className="p-8 text-center text-slate-500"><Users className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No enrolled players available</p><p className="text-xs mt-1">Select an age category first, or all players may already be registered</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => { setSortField('full_name'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Player {sortField === 'full_name' && (sortOrder === 'asc' ? '\u2191' : '\u2193')}</TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => { setSortField('age'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Age {sortField === 'age' && (sortOrder === 'asc' ? '\u2191' : '\u2193')}</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Eligible For</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrolledPlayers.filter(p => p.full_name?.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => {
                    const aVal = sortField === 'age' ? (a.age || 0) : (a.full_name || '');
                    const bVal = sortField === 'age' ? (b.age || 0) : (b.full_name || '');
                    if (sortField === 'age') return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                    return sortOrder === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
                  }).map(player => (
                    <TableRow key={player.id} data-testid={`enrolled-player-row-${player.id}`} className={`cursor-pointer ${selectedPlayerIds.includes(player.id) ? 'bg-emerald-50' : ''}`} onClick={() => onTogglePlayer(player.id)}>
                      <TableCell><Checkbox data-testid={`enrolled-player-checkbox-${player.id}`} checked={selectedPlayerIds.includes(player.id)} onCheckedChange={() => onTogglePlayer(player.id)} /></TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{player.full_name}</span>
                          {player.is_enrolled === false && (
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-300 text-[10px] gap-1"
                              data-testid={`graduated-badge-${player.id}`}
                              title="No active enrollment — graduated / seasonal player"
                            >
                              <GraduationCap className="h-3 w-3" />
                              Graduated
                            </Badge>
                          )}
                          {(!player.eligible_categories || player.eligible_categories.length === 0) && (
                            <Badge
                              variant="outline"
                              className="bg-orange-50 text-orange-700 border-orange-300 text-[10px]"
                              data-testid={`age-override-badge-${player.id}`}
                              title="Age-ineligible for this tournament's categories — admin override required"
                            >
                              Age Override
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{player.age || '-'}</TableCell>
                      <TableCell><div><p className="text-sm">{player.parent_name}</p><p className="text-xs text-slate-500">{player.parent_email}</p></div></TableCell>
                      <TableCell><div className="flex flex-wrap gap-1">{player.eligible_categories?.map(cat => (<Badge key={cat} variant="outline" className="text-xs">{cat.toUpperCase()}</Badge>))}</div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <p className="font-medium text-blue-800">Age Group Eligibility Rules (Play-Up)</p>
          <ul className="text-blue-700 text-xs mt-1 space-y-0.5">
            <li>U11 players can register for U11 and U13</li>
            <li>U13 players can register for U13 and U15</li>
            <li>U15 players can register for U15 and U17</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} disabled={selectedPlayerIds.length === 0 || addingPlayers} data-testid="add-enrolled-players-btn">
          {addingPlayers ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</>) : (<>Add {selectedPlayerIds.length} Player(s)</>)}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
